// Lightweight reverse geocoder with caching for browser use
// Uses OpenStreetMap Nominatim by default. Please respect their usage policy.

export type ReverseGeocodeResult = {
  label: string; // short: e.g., "Horfield, Bristol"
  fullLabel: string; // detailed: e.g., "Gloucester Rd, Horfield, Bristol, England BS7, United Kingdom"
  city?: string; // metropolitan area / major city
  district?: string; // district / borough / town
  region?: string; // state/county/province
  country?: string;
  postcode?: string;
  road?: string;
  neighborhood?: string;
  continent?: string;
};

const MEMO = new Map<string, { value: ReverseGeocodeResult; ts: number }>();
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const keyFor = (lat: number, lng: number) => `${lat.toFixed(4)},${lng.toFixed(4)}`;

export const getCachedLabel = (lat?: number, lng?: number): string => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return '';
  const k = keyFor(lat, lng);
  const m = MEMO.get(k);
  if (m && Date.now() - m.ts < TTL_MS) return m.value.label;
  try {
    const raw = localStorage.getItem(`revgeo:${k}`);
    if (!raw) return '';
    const obj = JSON.parse(raw);
    if (obj && obj.label && obj.ts && (Date.now() - obj.ts) < TTL_MS) {
      MEMO.set(k, { value: obj as ReverseGeocodeResult, ts: obj.ts });
      return obj.label as string;
    }
  } catch {}
  return '';
};

export const getCachedFullLabel = (lat?: number, lng?: number): string => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return '';
  const k = keyFor(lat, lng);
  const m = MEMO.get(k);
  if (m && Date.now() - m.ts < TTL_MS) return m.value.fullLabel;
  try {
    const raw = localStorage.getItem(`revgeo:${k}`);
    if (!raw) return '';
    const obj = JSON.parse(raw);
    if (obj && obj.fullLabel && obj.ts && (Date.now() - obj.ts) < TTL_MS) {
      MEMO.set(k, { value: obj as ReverseGeocodeResult, ts: obj.ts });
      return obj.fullLabel as string;
    }
  } catch {}
  return '';
};

const composeLabel = (addr: any): ReverseGeocodeResult => {
  const hood = addr?.neighbourhood || addr?.neighborhood || addr?.suburb || addr?.quarter;
  const district = addr?.city_district || addr?.state_district || addr?.town || addr?.borough || addr?.municipality;
  const locality = addr?.town || addr?.borough || addr?.municipality || addr?.village || addr?.hamlet;
  const cityLike = addr?.city || addr?.metropolitan_city || addr?.state_district || addr?.state || addr?.region || addr?.province;
  const region = addr?.state || addr?.province || addr?.region || addr?.county;
  const country = addr?.country;
  const postcode = addr?.postcode;
  const road = addr?.road || addr?.pedestrian || addr?.footway || addr?.residential;
  const continent = addr?.continent;

  // Short label prioritizes neighbourhood + city
  const shortParts: string[] = [];
  if (hood) shortParts.push(hood);
  if (district && !shortParts.some(p => p.toLowerCase() === String(district).toLowerCase())) shortParts.push(district);
  if (locality && !shortParts.some(p => p.toLowerCase() === String(locality).toLowerCase())) shortParts.push(locality);
  if (cityLike && !shortParts.some(p => p.toLowerCase() === String(cityLike).toLowerCase())) shortParts.push(cityLike);
  if (country && !shortParts.includes(country)) shortParts.push(country);
  const label = shortParts.join(', ') || cityLike || country || region || 'Location';

  // Full label includes road and broader context
  const fullParts: string[] = [];
  if (road) fullParts.push(road);
  if (hood) fullParts.push(hood);
  if (district && !fullParts.includes(district)) fullParts.push(district);
  if (locality && !fullParts.includes(locality)) fullParts.push(locality);
  if (cityLike) fullParts.push(cityLike);
  if (region) fullParts.push(region);
  if (postcode) fullParts.push(postcode);
  if (country) fullParts.push(country);
  const fullLabel = fullParts.filter(Boolean).join(', ') || label;

  return {
    label,
    fullLabel,
    city: cityLike,
    district: district || locality,
    region,
    country,
    postcode,
    road,
    neighborhood: hood,
    continent
  };
};

export const reverseGeocode = async (lat: number, lng: number, signal?: AbortSignal): Promise<ReverseGeocodeResult> => {
  const k = keyFor(lat, lng);
  const cached = getCachedLabel(lat, lng);
  if (cached) {
    const m = MEMO.get(k)!;
    return m.value;
  }
  const base = (process.env.REACT_APP_REVERSE_GEOCODE_URL || '').trim();
  const url = base
    ? base.replace('{lat}', String(lat)).replace('{lng}', String(lng))
    : `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&zoom=16&addressdetails=1`;
  try {
    const res = await fetch(url, {
      method: 'GET',
      signal,
      headers: { 'Accept-Language': 'en' },
    });
    const data = await res.json();
    const addr = data?.address || {};
    const value = composeLabel(addr);
    const rec = { ...value } as ReverseGeocodeResult;
    const storage = { ...rec, ts: Date.now() } as any;
    MEMO.set(k, { value: rec, ts: storage.ts });
    try { localStorage.setItem(`revgeo:${k}`, JSON.stringify(storage)); } catch {}
    return rec;
  } catch {
    // Fallback minimal result; avoid throwing to callers
    const value = { label: 'Location', fullLabel: 'Location', city: undefined, region: undefined, country: undefined } as ReverseGeocodeResult;
    const storage = { ...value, ts: Date.now() } as any;
    MEMO.set(k, { value, ts: storage.ts });
    try { localStorage.setItem(`revgeo:${k}`, JSON.stringify(storage)); } catch {}
    return value;
  }
};
